import React, { useMemo } from 'react'
import L from 'leaflet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import type { OmniCategory } from './controlCenterMapTypes'
import type { EntityDragPayload } from '../entityDrag'
import type { OmniSelection } from './controlCenterMapTypes'
import { entityRef } from '../operations'
import { useDomEntityDrag } from './useEntityDrag'

export function OmniPinnedBadge({
  badge,
  focused,
  onFocus,
  onRemove,
  mapRef,
}: {
  badge: { id: string; category: OmniCategory; name: string; color: string }
  focused: boolean
  onFocus: (id: string, category: OmniCategory) => void
  onRemove: (id: string) => void
  mapRef: React.RefObject<L.Map | null>
}) {
  const dragPayload = useMemo(
    (): EntityDragPayload => ({
      refId: entityRef(badge.category, badge.id),
      label: badge.name,
      color: badge.color,
    }),
    [badge.category, badge.id, badge.name, badge.color],
  )

  const { onPointerDown, suppressClickRef } = useDomEntityDrag(dragPayload, mapRef)

  return (
    <span
      className={`inline-flex max-w-full items-center gap-0.5 rounded-full border-2 py-0.5 pl-1 pr-1 text-xs shadow-md bg-base-100 ${
        focused ? 'border-primary' : 'border-base-300'
      }`}
    >
      <button
        type="button"
        className="inline-flex min-w-0 max-w-full cursor-grab touch-none items-center gap-1.5 rounded-full px-1.5 py-0.5 text-left hover:bg-base-200 active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onClick={() => {
          if (suppressClickRef.current) return
          onFocus(badge.id, badge.category)
        }}
        title={`Focus ${badge.name} · drag into a destination field`}
        aria-label={`Focus ${badge.name}`}
        aria-pressed={focused}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: badge.color }}
          aria-hidden
        />
        <span className="min-w-0 truncate font-medium">{badge.name}</span>
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-xs btn-circle h-5 min-h-0 w-5"
        aria-label={`Remove ${badge.name}`}
        onClick={() => onRemove(badge.id)}
      >
        <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
      </button>
    </span>
  )
}

